import _ from 'lodash';
import { List } from 'immutable';

import {
    TicketTypes,
    StationTypes,
    SeasonStationTypes,
    SmartcardStationTypes,
    SmartcardSeasonStationTypes,
    JourneyDirections,
    DisruptionAlertDurationOptions,
    DisruptionAlertDaysOptions,
    DisruptionAlertWeekdays,
    TransitModes
} from '../constants/AppConstants';
import trainTime from '../lib/TrainTime';
import Search from '../records/Search';
import SeasonSearch from '../records/SeasonSearch';
import DaySaveSearch from '../records/DaySaveSearch';
import Station from '../records/Station';
import DisruptionAlertStore from '../stores/DisruptionAlertStore';
import CustomerServiceChangeLeg from '../records/CustomerServiceChangeLeg';
import DisruptionAlertLeg from '../records/DisruptionAlertLeg';


export const formatDisruptionAlertResponse = ({
    disruptionAlertId,
    originStationDetail,
    destinationStationDetail,
    legs,
    mutes,
    ...response
}) => ({
    ...response,
    id: disruptionAlertId,
    origin: originStationDetail,
    destination: destinationStationDetail,
    legs: legs.map(({
        journeyLegId,
        originStationDetail: legOriginStationDetail,
        destinationStationDetail: legDestinationStationDetail,
        ...leg
    }) => ({
        ...leg,
        id: journeyLegId,
        origin: legOriginStationDetail,
        destination: legDestinationStationDetail,
    })),
    mutes: mutes.map(({ muteId, ...mute }) => ({
        ...mute,
        id: muteId,
    })),
});

export const formatDisruptionAlertMuteResponse = ({ muteId, ...response }) => ({ ...response, id: muteId });

export const filterSameDayDeparturesOnly = (servicesList, searchDate) =>
    servicesList.filter(s => s.getDepartureDateTime().isSame(searchDate, 'day'));

const isInward = direction => direction === JourneyDirections.INWARD;

export const getInitialFormState = (alert, order, orderTrip, trip, isSeason, isDaySave, journeyDirection, prefillFromSearch) => {
    let search = new Search();
    const defaultStartDate = search.getDefaultOutboundDateTime();

    let formState = {
        origin: new Station(),
        destination: new Station(),
        startDate: defaultStartDate,
        selectedServices: new List(),
        duration: DisruptionAlertDurationOptions[isSeason ? 'SIXMONTHS' : 'ONEOFF'],
        customEndDate: null,
        days: getDaysOptionFromDate(defaultStartDate),
        weekdays: _.mapValues(DisruptionAlertWeekdays, () => true),
    };

    if (alert) {
        search = createSearchFromAlert(alert, journeyDirection);

        const { origin, destination, departureDate, departureTime, alertFrom, alertTo, daysOfWeek } = alert;

        let startDate = trainTime();
        if (departureDate && departureTime) {
            startDate = departureDate.clone().set({
                hour: departureTime.hour(),
                minute: departureTime.minute(),
            });
        } else if (alertFrom && departureTime) {
            startDate = alertFrom.clone().set({
                hour: departureTime.hour(),
                minute: departureTime.minute(),
            });
        }

        let duration;
        if (alert.isForever()) {
            duration = DisruptionAlertDurationOptions.INFINITE;
        } else if (alertFrom.isSame(alertTo)) {
            duration = DisruptionAlertDurationOptions.ONEOFF;
        } else if (alertFrom.clone().add(6, 'months').isSame(alertTo)) {
            duration = DisruptionAlertDurationOptions.SIXMONTHS;
        } else {
            duration = DisruptionAlertDurationOptions.CUSTOM;
        }

        let days;
        const containsWeekdays = !daysOfWeek.isEmpty() &&
            daysOfWeek.every(day => _.values(DisruptionAlertWeekdays).includes(day));

        if (daysOfWeek === ['SATURDAY'] || departureDate && isSaturday(departureDate)) {
            days = DisruptionAlertDaysOptions.SATURDAYS;
        } else if (daysOfWeek === ['SUNDAY'] || departureDate && isSunday(departureDate)) {
            days = DisruptionAlertDaysOptions.SUNDAYS;
        } else if (containsWeekdays || departureDate && isWeekday(departureDate)) {
            days = DisruptionAlertDaysOptions.WEEKDAYS;
        }

        const weekdays = containsWeekdays
            ? _.mapValues(DisruptionAlertWeekdays, day => daysOfWeek.includes(day))
            : _.mapValues(DisruptionAlertWeekdays, () => true);

        formState = {
            ...formState,
            origin: isInward(journeyDirection) ? destination : origin,
            destination: isInward(journeyDirection) ? origin : destination,
            startDate,
            duration,
            customEndDate: alertTo,
            days,
            weekdays,
        };
    } else {
        if (order && orderTrip) {
            // Should only ever be Season or Day Save tickets
            search = createSearchFromOrderTrip(orderTrip, isDaySave, journeyDirection);
        } else if (trip && prefillFromSearch) {
            const { search: tripSearch } = trip;
            if (tripSearch instanceof SeasonSearch) {
                search = createSearchFromSeasonSearch(tripSearch, journeyDirection);
            } else if (tripSearch instanceof DaySaveSearch) {
                search = createSearchFromDaySaveSearch(tripSearch, journeyDirection);
            } else {
                search = createSearchFromTripSearch(tripSearch, journeyDirection);
            }
        }

        const origin = !search.origin.isGroupStation() ? search.origin : new Station();
        const destination = !search.destination.isGroupStation() ? search.destination : new Station();
        const startDate = search.getOutboundDateTime();

        formState = {
            ...formState,
            origin,
            destination,
            startDate,
            days: getDaysOptionFromDate(startDate),
        };
    }

    return [
        search,
        formState,
    ];
};

export const checkForReturnJourney = (orderTrip, trip, journeyDirection, prefillFromSearch) => {
    const isOutward = journeyDirection === JourneyDirections.OUTWARD;
    const hasOrderTripReturn = orderTrip && (orderTrip.hasReturnJourney() || orderTrip.isSeason() || orderTrip.hasDaySaveFare());
    const hasSearchTripReturn = trip && (trip.hasReturnFare() || trip.isSeason() || trip.isDaySave());

    return (prefillFromSearch && isOutward && (hasOrderTripReturn || hasSearchTripReturn));
};

export const getStationType = (smartcard, isSeason) => {
    let stationType = StationTypes;

    if (smartcard && isSeason) {
        stationType = SmartcardSeasonStationTypes;
    } else if (smartcard) {
        stationType = SmartcardStationTypes;
    } else if (isSeason) {
        stationType = SeasonStationTypes;
    }

    return stationType;
};

const createSearchFromAlert = (alert, journeyDirection) => {
    const search = new Search({
        origin: isInward(journeyDirection) ? alert.destination : alert.origin,
        destination: isInward(journeyDirection) ? alert.origin : alert.destination,
        ticketType: TicketTypes.SINGLE,
    });

    const outboundDateTime = alert.departureDate || alert.alertFrom;

    if (outboundDateTime) {
        return search.setOutboundDateTime(outboundDateTime);
    }

    return search;
};

const createSearchFromOrderTrip = (orderTrip, isDaySave, journeyDirection) => new Search({
    origin: isDaySave
        ? (isInward(journeyDirection) ? new Station() : orderTrip.getDaySaveFare().origin)
        : (isInward(journeyDirection) ? orderTrip.getDestination() : orderTrip.getFirstOrigin()),
    destination: isDaySave
        ? (isInward(journeyDirection) ? orderTrip.getDaySaveFare().origin : new Station())
        : (isInward(journeyDirection) ? orderTrip.getFirstOrigin() : orderTrip.getDestination()),
    ticketType: TicketTypes.SINGLE,
}).setOutboundDateTime(
    isDaySave ? orderTrip.getDaySaveFare().outwardJourney.validFrom.clone().hours(12) : (
        orderTrip.isSeason()
            ? orderTrip.getSeasonFare().getValidFrom().clone().set({ hours: 12, minutes: 0 })
            : orderTrip.getDepartureTime()
    )
);

const createSearchFromTripSearch = (search, journeyDirection) => new Search({
    origin: search[isInward(journeyDirection) ? 'destination' : 'origin'],
    destination: search[isInward(journeyDirection) ? 'origin' : 'destination'],
    ticketType: TicketTypes.SINGLE,
}).setOutboundDateTime(isInward(journeyDirection) ? search.getReturnDateTime() : search.getOutboundDateTime());

const createSearchFromSeasonSearch = (seasonSearch, journeyDirection) => {
    let outboundDateTime = seasonSearch.getStartDate().clone().hours(12);
    if (isInward(journeyDirection)) {
        outboundDateTime.add(6, 'days');
    }

    return new Search({
        origin: seasonSearch[isInward(journeyDirection) ? 'destination' : 'origin'],
        destination: seasonSearch[isInward(journeyDirection) ? 'origin' : 'destination'],
        ticketType: TicketTypes.SINGLE,
    }).setOutboundDateTime(outboundDateTime);
};

const createSearchFromDaySaveSearch = (daySaveSearch, journeyDirection) => new Search({
    origin: isInward(journeyDirection) ? new Station() : daySaveSearch.origin,
    destination: isInward(journeyDirection) ? daySaveSearch.origin : new Station(),
}).setOutboundDateTime(daySaveSearch.date.clone().hours(12));

export const isWeekday = datetime => datetime.isoWeekday() >= 1 && datetime.isoWeekday() <= 5;
export const isSaturday = datetime => datetime.isoWeekday() === 6;
export const isSunday = datetime => datetime.isoWeekday() === 7;
export const isBankHoliday = datetime => DisruptionAlertStore.getBankHolidays().find(date => date.isSame(datetime, 'day'));

export const getDaysOptionFromDate = datetime => DisruptionAlertDaysOptions[
    isSaturday(datetime) ? 'SATURDAYS' :
    isSunday(datetime) ? 'SUNDAYS' :
    'WEEKDAYS'
];

// e.g. 'MONDAY' => 'Monday' or 'Mon'
export const formatDayOfWeek = (dayStr, abbreviated = false) =>
    dayStr
        .substring(0, abbreviated ? 3 : undefined)
        .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());

export const formatLegsAsCustomerServiceChangeLeg = (legs) => {
    return legs
        .filter(leg => leg.mode === TransitModes.TRAIN)
        .map((leg, legIndex) => new CustomerServiceChangeLeg({
            trainUid: leg.tuid,
            origin: leg.origin.crs,
            originStationDetail: null,
            destination: leg.destination.crs,
            destinationStationDetail: null,
            departureDate: leg.departure,
            departureTime: leg.departure,
            arrivalTime: leg.arrival,
            legOrder: ++legIndex,
        }));
};

export const legsToDisruptionAlertLegs = (legs) => {
    return legs
        .filter(l => l.mode === TransitModes.TRAIN)
        .map((leg, legIndex) =>
            new DisruptionAlertLeg({
                trainUid: leg.tuid,
                origin: leg.origin,
                destination: leg.destination,
                departureTime: leg.departure,
                arrivalTime: leg.arrival,
                groupId: legIndex + 1,
                legOrder: ++legIndex,
            })
        );
};



// WEBPACK FOOTER //
// ./src/helpers/DisruptionAlert.js